import React, {useCallback} from "react";

type SeveritySelectorProps = {
    selectedSeverities: string[];
    onSelectedSeveritiesChange: (severities: string[]) => void;
}

const SEVERITIES = [
    { value: "extreme", label: "Extreme", color: "bg-purple text-white" },
    { value: "major", label: "Major", color: "bg-dark-red text-white" },
    { value: "moderate", label: "Moderate", color: "bg-dark-orange text-white" },
    { value: "limited", label: "Limited", color: "bg-[#D8B700] text-white" },
    { value: "insignificant", label: "Insignificant", color: "bg-green text-white" },
    { value: "unknown", label: "Unknown", color: "bg-grey text-white" },
];

export function SeveritySelector({selectedSeverities, onSelectedSeveritiesChange}: SeveritySelectorProps) {
    const handleToggle = useCallback((severity: string) => {
        if (selectedSeverities.includes(severity)) {
            onSelectedSeveritiesChange(selectedSeverities.filter(s => s !== severity));
        } else {
            onSelectedSeveritiesChange([...selectedSeverities, severity]);
        }
    }, [selectedSeverities, onSelectedSeveritiesChange]);

    const handleSelectAll = useCallback(() => {
        if (selectedSeverities.length === SEVERITIES.length) {
            onSelectedSeveritiesChange([]);
        } else {
            onSelectedSeveritiesChange(SEVERITIES.map(s => s.value));
        }
    }, [selectedSeverities, onSelectedSeveritiesChange]);

    const allSelected = selectedSeverities.length === SEVERITIES.length;

    return (
        <div className="bg-white border border-light-grey rounded p-4">
            <div className="flex justify-between items-center mb-3">
                <span className="font-medium">Incident Severity Levels</span>
                <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-sm text-blue hover:underline"
                >
                    {allSelected ? "Deselect All" : "Select All"}
                </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
                {SEVERITIES.map((severity) => (
                    <div
                        key={severity.value}
                        onClick={() => handleToggle(severity.value)}
                        className={`flex items-center gap-3 p-3 border rounded hover:bg-light-light-grey cursor-pointer ${
                            selectedSeverities.includes(severity.value) ? "bg-blue-50 border-blue" : "border-light-grey"
                        }`}
                    >
                        <input
                            type="checkbox"
                            checked={selectedSeverities.includes(severity.value)}
                            onChange={() => {}} // Handled by div onClick
                            className="w-4 h-4 accent-blue cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold ${severity.color}`}>
                            {severity.label}
                        </span>
                    </div>
                ))}
            </div>

            <div className="text-sm text-grey mt-3">
                {selectedSeverities.length} of {SEVERITIES.length} selected
            </div>
        </div>
    );
}
